/**
 * Shared hook for AG-UI conversation persistence.
 *
 * Provides cache-first history loading, in-progress run detection,
 * streaming state tracking, and run cancellation for any AG-UI agent.
 *
 * Usage:
 *   const persistence = useAgUiPersistence({ agent, runtime, api, conversationId });
 *   // → { isLoadingHistory, isStreaming, isRunningRemotely, isCancelling, onCancelRun }
 */
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import type { HttpAgent } from "@ag-ui/client";
import type { AssistantRuntime, ThreadMessageLike } from "@assistant-ui/react";
import type { AssistantApi } from "~/components/assistant/api/AssistantApi";
import type { AssistantMessageCustomMetadata, TurnFeedback } from "~/components/assistant/types";
import { apiClient, isApiError } from "~/utils/apiClient";

// ---------------------------------------------------------------------------
// Module-level caches — persist for the SPA lifetime
// ---------------------------------------------------------------------------

const activeIds = new Map<string, string>();

// ---------------------------------------------------------------------------
// Running conversations tracker — reactive via useSyncExternalStore
// ---------------------------------------------------------------------------

let runningSnapshot = new Set<string>();
const runningListeners = new Set<() => void>();

function notifyRunningListeners() {
  runningListeners.forEach((l) => l());
}

function markConversationRunning(id: string) {
  if (!runningSnapshot.has(id)) {
    const next = new Set(runningSnapshot);
    next.add(id);
    runningSnapshot = next;
    notifyRunningListeners();
  }
}

function markConversationIdle(id: string) {
  if (runningSnapshot.has(id)) {
    const next = new Set(runningSnapshot);
    next.delete(id);
    runningSnapshot = next;
    notifyRunningListeners();
  }
}

function subscribeRunning(callback: () => void) {
  runningListeners.add(callback);
  return () => {
    runningListeners.delete(callback);
  };
}

function getRunningSnapshot() {
  return runningSnapshot;
}

/**
 * Reactive hook returning the set of conversation IDs with active runs.
 * Re-renders only when the set changes.
 */
export function useRunningConversations(): Set<string> {
  return useSyncExternalStore(subscribeRunning, getRunningSnapshot, getRunningSnapshot);
}

interface HistoryCacheEntry {
  messages: ThreadMessageLike[];
  cachedAt: number;
}
const historyCache = new Map<string, HistoryCacheEntry>();
const MAX_CACHED = 10;

/** Get the last-viewed conversation ID for a given context (e.g. dataset). */
export function getActiveConversationId(contextId: string): string | null {
  return activeIds.get(contextId) ?? null;
}

/** Remember which conversation was last viewed for a context. */
export function setActiveConversationId(contextId: string, conversationId: string | null): void {
  if (conversationId) {
    activeIds.set(contextId, conversationId);
  } else {
    activeIds.delete(contextId);
  }
}

function getCachedHistory(conversationId: string): ThreadMessageLike[] | null {
  return historyCache.get(conversationId)?.messages ?? null;
}

function setCachedHistory(conversationId: string, messages: ThreadMessageLike[]): void {
  historyCache.set(conversationId, { messages, cachedAt: Date.now() });
  if (historyCache.size > MAX_CACHED) {
    const sorted = Array.from(historyCache.entries()).sort(
      ([, a], [, b]) => a.cachedAt - b.cachedAt
    );
    for (const [id] of sorted) {
      if (historyCache.size <= MAX_CACHED) {
        break;
      }
      historyCache.delete(id);
    }
  }
}

// ---------------------------------------------------------------------------
// AG-UI → assistant-ui message conversion
// ---------------------------------------------------------------------------

interface AguiToolCall {
  id: string;
  type: string;
  function: { name: string; arguments: string };
}

interface AguiMessage {
  role: "user" | "assistant" | "tool";
  content: string | null;
  id?: string;
  toolCalls?: AguiToolCall[];
  toolCallId?: string;
  artifactNames?: string[];
  cancelled?: boolean;
  turnId?: string;
  turnFeedback?: TurnFeedback | null;
}

type ContentPart =
  | { readonly type: "text"; readonly text: string }
  | {
      readonly type: "tool-call";
      readonly toolCallId: string;
      readonly toolName: string;
      readonly args: Record<string, unknown>;
      readonly argsText: string;
      result?: string | null;
    };

/**
 * Merges consecutive assistant + tool messages into a single assistant
 * message per user turn, matching the structure assistant-ui expects.
 */
export function convertAguiToThreadMessages(aguiMessages: AguiMessage[]): ThreadMessageLike[] {
  const result: ThreadMessageLike[] = [];
  let currentParts: ContentPart[] = [];
  let lastTurnId: string | undefined;
  let lastTurnFeedback: TurnFeedback | null | undefined;
  let pendingCancelled = false;
  let pendingArtifactNames: string[] | undefined;

  const flushAssistant = () => {
    if (currentParts.length > 0 || pendingCancelled) {
      const custom: AssistantMessageCustomMetadata = {};
      if (lastTurnId) {
        custom.turnId = lastTurnId;
      }
      if (lastTurnFeedback) {
        custom.feedback = lastTurnFeedback;
      }
      if (pendingCancelled) {
        custom.cancelled = true;
      }
      if (pendingArtifactNames && pendingArtifactNames.length > 0) {
        custom.artifactNames = pendingArtifactNames;
      }
      result.push({
        role: "assistant",
        content: currentParts as ThreadMessageLike["content"],
        metadata: Object.keys(custom).length > 0 ? { custom } : undefined,
      });
      currentParts = [];
      pendingCancelled = false;
      pendingArtifactNames = undefined;
    }
  };

  for (const msg of aguiMessages) {
    if (msg.role === "user") {
      flushAssistant();
      lastTurnId = msg.turnId;
      lastTurnFeedback = msg.turnFeedback;
      result.push({ role: "user", content: msg.content ?? "" });
    } else if (msg.role === "assistant") {
      if (msg.cancelled) {
        pendingCancelled = true;
        continue;
      }
      if (msg.toolCalls) {
        for (const tc of msg.toolCalls) {
          let args: Record<string, unknown> = {};
          try {
            args = JSON.parse(tc.function.arguments);
          } catch {
            // keep empty args if parse fails
          }
          currentParts.push({
            type: "tool-call",
            toolCallId: tc.id,
            toolName: tc.function.name,
            args,
            argsText: tc.function.arguments,
          });
        }
      }
      if (msg.content && !msg.toolCalls) {
        currentParts.push({ type: "text", text: msg.content });
        if (msg.artifactNames && msg.artifactNames.length > 0) {
          pendingArtifactNames = msg.artifactNames;
        }
      }
    } else if (msg.role === "tool") {
      for (let i = currentParts.length - 1; i >= 0; i--) {
        const part = currentParts[i];
        if (part.type === "tool-call" && part.toolCallId === msg.toolCallId) {
          part.result = msg.content;
          break;
        }
      }
    }
  }

  flushAssistant();
  return result;
}

export async function fetchConversationHistory(
  baseUrl: string,
  conversationId: string
): Promise<ThreadMessageLike[]> {
  try {
    const resp = await apiClient<{ state: unknown; messages: AguiMessage[] }>(
      `${baseUrl}/conversations/${conversationId}/connect`
    );
    return resp.messages.length ? convertAguiToThreadMessages(resp.messages) : [];
  } catch (error) {
    if (isApiError(error) && error.status === 400) {
      return [];
    }
    throw error;
  }
}

// ---------------------------------------------------------------------------
// The hook
// ---------------------------------------------------------------------------

const POLL_INTERVAL_MS = 2000;

function isInProgress(status: string | null | undefined): boolean {
  return status === "MESSAGE_IN_PROGRESS" || status === "MESSAGE_CANCELLED";
}

interface UseAgUiPersistenceOptions {
  agent: HttpAgent;
  runtime: AssistantRuntime;
  api: AssistantApi;
  conversationId: string | null;
  clearError?: () => void;
  /** Pre-converted messages from /connect — skips the /messages_agui fetch. */
  initialMessages?: ThreadMessageLike[] | null;
}

interface UseAgUiPersistenceReturn {
  isLoadingHistory: boolean;
  /** True after history loading completes and at least one message was found. */
  historyHasMessages: boolean;
  isStreaming: boolean;
  isRunningRemotely: boolean;
  isCancelling: boolean;
  onCancelRun: () => void;
}

/**
 * AG-UI conversation persistence hook.
 *
 * The parent component MUST key this hook's host on `conversationId` so that
 * the entire runtime remounts fresh on every conversation switch. This
 * guarantees `conversationId` is stable for the lifetime of the hook and
 * eliminates timing races between assistant-ui's external store and React
 * state that previously caused flickers and ghost indicators.
 */
export function useAgUiPersistence({
  agent,
  runtime,
  api,
  conversationId,
  clearError,
  initialMessages,
}: UseAgUiPersistenceOptions): UseAgUiPersistenceReturn {
  const { baseUrl } = api;
  const [isLoadingHistory, setIsLoadingHistory] = useState(() => !!conversationId);
  const [historyHasMessages, setHistoryHasMessages] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isRunningRemotely, setIsRunningRemotely] = useState(
    () => !!conversationId && runningSnapshot.has(conversationId)
  );
  const [isCancelling, setIsCancelling] = useState(false);

  const abortPollingRef = useRef<(() => void) | null>(null);

  // --- Shared helper: fetch history from server → cache + reset thread ---

  const reloadHistory = useCallback(
    async (cid: string) => {
      try {
        const messages = await fetchConversationHistory(baseUrl, cid);
        if (messages.length > 0) {
          setCachedHistory(cid, messages);
          runtime.thread.reset(messages);
        }
      } catch (error) {
        console.error("Failed to reload history:", error);
      }
    },
    [baseUrl, runtime]
  );

  // --- Poll server status until run completes, then fetch result once ---

  const pollUntilReady = useCallback(
    (cid: string) => {
      abortPollingRef.current?.();
      let aborted = false;
      abortPollingRef.current = () => {
        aborted = true;
      };

      setIsRunningRemotely(true);
      markConversationRunning(cid);

      (async () => {
        while (!aborted) {
          await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
          if (aborted) {
            return;
          }

          try {
            const resp = await api.getConversationStatus(cid);
            if (aborted) {
              return;
            }

            if (!isInProgress(resp.status)) {
              setIsRunningRemotely(false);
              setIsCancelling(false);
              markConversationIdle(cid);
              await reloadHistory(cid);
              return;
            }
          } catch (error) {
            console.error("Failed to poll status:", error);
          }
        }
      })();
    },
    [api, reloadHistory]
  );

  // --- 1. Load history on mount ---
  // Prefers initialMessages from /connect (no extra fetch), falls back to
  // the in-memory cache, then to a /messages_agui server fetch.

  useEffect(() => {
    if (!conversationId) {
      setIsLoadingHistory(false);
      return;
    }

    let active = true;

    (async () => {
      let loaded = false;

      // 1a. /connect already gave us the messages — use them directly.
      if (initialMessages && initialMessages.length > 0) {
        setCachedHistory(conversationId, initialMessages);
        runtime.thread.reset(initialMessages);
        loaded = true;
      } else if (!initialMessages) {
        // 1b. No pre-fetched messages — try cache, then server.
        const cached = getCachedHistory(conversationId);
        if (cached?.length) {
          runtime.thread.reset(cached);
          loaded = true;
        } else {
          try {
            const messages = await fetchConversationHistory(baseUrl, conversationId);
            if (!active) {
              return;
            }
            if (messages.length > 0) {
              setCachedHistory(conversationId, messages);
              runtime.thread.reset(messages);
              loaded = true;
            }
          } catch (error) {
            console.error("Failed to load conversation history:", error);
          }
        }
      }

      if (active) {
        setHistoryHasMessages(loaded);
        setIsLoadingHistory(false);
      }
    })();

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, runtime]);

  // --- 2. Detect in-progress runs on mount ---

  useEffect(() => {
    if (!conversationId) {
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const resp = await api.getConversationStatus(conversationId);
        if (cancelled) {
          return;
        }
        if (isInProgress(resp.status)) {
          pollUntilReady(conversationId);
        } else {
          const wasRunning = runningSnapshot.has(conversationId);
          markConversationIdle(conversationId);
          setIsRunningRemotely(false);
          if (wasRunning) {
            reloadHistory(conversationId);
          }
        }
      } catch (error) {
        console.error("Failed to fetch conversation status:", error);
      }
    })();

    return () => {
      cancelled = true;
      abortPollingRef.current?.();
      abortPollingRef.current = null;
    };
  }, [api, conversationId, pollUntilReady, reloadHistory]);

  // --- 3. Track local streaming + sync cache after run completes ---

  useEffect(() => {
    if (!conversationId) {
      return;
    }

    const cid = conversationId;
    const sub = agent.subscribe({
      onRunStartedEvent: () => {
        setIsStreaming(true);
        markConversationRunning(cid);
        const currentMessages = runtime.thread.getState().messages;
        if (currentMessages.length > 0) {
          setCachedHistory(cid, [...currentMessages.filter((m) => m.status?.type !== "running")]);
        }
      },
      onRunFinishedEvent: () => {
        setIsStreaming(false);
        markConversationIdle(cid);
        // Fetch from the server so the cache gets proper metadata
        // (turnId, artifactNames) that streaming messages don't carry.
        reloadHistory(cid);
      },
      onRunFailed: () => {
        setIsStreaming(false);
        markConversationIdle(cid);
      },
    });

    return () => {
      sub.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent, conversationId]);

  // --- 4. Cancel handler ---

  const onCancelRun = useCallback(() => {
    if (!conversationId) {
      return;
    }

    clearError?.();
    agent.abortRun();
    setIsCancelling(true);

    api.cancelMessage(conversationId).then(
      async () => {
        try {
          const resp = await api.getConversationStatus(conversationId);
          if (!isInProgress(resp.status)) {
            setIsCancelling(false);
            setIsRunningRemotely(false);
            markConversationIdle(conversationId);
            await reloadHistory(conversationId);
          } else {
            pollUntilReady(conversationId);
          }
        } catch {
          setIsCancelling(false);
          setIsRunningRemotely(false);
          markConversationIdle(conversationId);
        }
      },
      (error) => {
        console.error("Failed to cancel run:", error);
        setIsCancelling(false);
        setIsRunningRemotely(false);
        markConversationIdle(conversationId);
      }
    );
  }, [api, agent, conversationId, clearError, reloadHistory, pollUntilReady]);

  return {
    isLoadingHistory,
    historyHasMessages,
    isStreaming,
    isRunningRemotely,
    isCancelling,
    onCancelRun,
  };
}
